import { createFeature, createReducer, on } from '@ngrx/store';
import { qcropActions } from '../actions/qcrop.actions';
import { Frame, Image, QcropModel, UiTemplate } from '../../model/qcrop.model';
import { QcropStateModel } from '../../model/qcrop-state.model';
import { transformQcrop } from '../../utils/transform.util';
import { hasDifference } from '../../utils/compare.util';
import { isEqual } from 'lodash-es';

export interface QcropFeature extends QcropStateModel {
  assetId: string | null;
  image: Image | null;
  qcrop: QcropModel | null;
  _qcrop: QcropModel | null;
  modified: boolean;
  message: string | null;
  companyId: string | null;
  token: string | null;
}

export const ininitialQcropState: QcropFeature = {
  assetId: null,
  image: null,
  qcrop: null,
  _qcrop: null,
  loading: false,
  modified: false,
  message: null,
  companyId: null,
  token: null,
};

export const qcropFeature = createFeature({
  name: 'qcrop',
  reducer: createReducer(
    ininitialQcropState,
    on(qcropActions.setActiveAssetId, (state, { assetId }) => ({
      ...state,
      assetId,
    })),
    on(qcropActions.loadQcrop, state => ({
      ...state,
      loading: true,
    })),
    on(qcropActions.loadQcropSuccess, (state, { qcrop }) => ({
      ...state,
      qcrop,
      loading: false,
    })),
    on(qcropActions.loadQcropFailed, (state, { error }) => ({
      ...state,
      loading: false,
      error,
    })),
    on(qcropActions.saveQcrop, state => ({
      ...state,
      loading: true,
    })),
    on(qcropActions.saveQcropSuccess, (state, { qcrop }) => ({
      ...state,
      qcrop,
      _qcrop: state._qcrop
        ? {
            ...state._qcrop,
            templates: state._qcrop.templates.map(template => ({
              ...template,
              modified: null,
            })),
          }
        : null,
      modified: false,
      loading: false,
    })),
    on(qcropActions.saveQcropFailed, (state, { error }) => ({
      ...state,
      loading: false,
      error,
    })),
    on(qcropActions.set_qcrop, (state, { qcrop }) => ({
      ...state,
      _qcrop: qcrop,
    })),
    on(qcropActions.modifyUiTemplate, (state, { uiTemplate, index }) => {
      const newQcrop = {
        ...state,
        _qcrop: state._qcrop
          ? {
              ...state._qcrop,
              templates: state._qcrop.templates.map((template, i) =>
                i === index ? { ...uiTemplate, modified: true } : template
              ),
            }
          : null,
      };

      newQcrop.modified = hasDifference(
        state.qcrop,
        removeUiProperties(newQcrop._qcrop)
      );
      return newQcrop;
    }),
    on(qcropActions.reload_qcrop, state => ({
      ...state,
      _qcrop: state.qcrop ? transformQcrop(state.qcrop) : null,
      modified: false,
    })),
    on(qcropActions.setImage, (state, { image }) => ({
      ...state,
      image,
    })),
    on(qcropActions.modifyFocalPoint, (state, { focalPoint, index }) => {
      const newQcrop = {
        ...state,
        _qcrop: state._qcrop
          ? {
              ...state._qcrop,
              focalPoints: state._qcrop.focalPoints.map((_focalPoint, i) =>
                i === index ? focalPoint : _focalPoint
              ),
            }
          : null,
      };

      newQcrop.modified = !isEqual(
        state.qcrop,
        removeUiProperties(newQcrop._qcrop)
      );
      return newQcrop;
    }),
    on(qcropActions.removeFocalPoint, (state, { index }) => {
      const newQcrop = {
        ...state,
        _qcrop: state._qcrop
          ? {
              ...state._qcrop,
              focalPoints: state._qcrop.focalPoints.filter(
                (_focalPoint, i) => i !== index
              ),
            }
          : null,
      };

      newQcrop.modified = !isEqual(
        state.qcrop,
        removeUiProperties(newQcrop._qcrop)
      );
      return newQcrop;
    }),
    on(qcropActions.addFocalPoint, (state, { focalPoint }) => ({
      ...state,
      _qcrop: state._qcrop
        ? {
            ...state._qcrop,
            focalPoints: [
              ...state._qcrop.focalPoints,
              {
                ...focalPoint,
                visible: true,
                name: `${focalPoint.name}_${state._qcrop.focalPoints.length}`,
              },
            ],
          }
        : null,
      modified: true,
    })),
    on(qcropActions.toggleVisibilityAllFocalPoints, (state, { visible }) => ({
      ...state,
      _qcrop: state._qcrop
        ? {
            ...state._qcrop,
            focalPoints: state._qcrop.focalPoints.map(focalPoint => ({
              ...focalPoint,
              visible,
            })),
          }
        : null,
    })),
    on(qcropActions.toggleVisibilityAllUiTemplates, (state, { visible }) => ({
      ...state,
      _qcrop: state._qcrop
        ? {
            ...state._qcrop,
            templates: state._qcrop.templates.map(template => ({
              ...template,
              visible,
            })),
          }
        : null,
    })),
    on(qcropActions.setActiveFrame, (state, { uiTemplate, index }) => ({
      ...state,
      _qcrop: state._qcrop
        ? {
            ...state._qcrop,
            templates: state._qcrop.templates.map((template, i) =>
              i === index ? uiTemplate : template
            ),
          }
        : null,
    })),
    on(qcropActions.setActiveFocalPoint, (state, { focalPoint, index }) => ({
      ...state,
      _qcrop: state._qcrop
        ? {
            ...state._qcrop,
            focalPoints: state._qcrop.focalPoints.map((_focalPoint, i) =>
              i === index ? focalPoint : _focalPoint
            ),
          }
        : null,
    })),
    on(qcropActions.autoGenerateFrames, (state, { templateIds }) => ({
      ...state,
      loading: true,
      error: null,
    })),
    on(qcropActions.autoGenerateFramesSuccess, (state, { frames }) => {
      const newQcrop = {
        ...state,
        _qcrop: mergeQcrop(state._qcrop, frames),
        loading: false,
        error: null,
      };

      newQcrop.modified = !isEqual(
        state.qcrop,
        removeUiProperties(newQcrop._qcrop)
      );

      return newQcrop;
    }),
    on(qcropActions.autoGenerateFrameFailed, (state, { error }) => ({
      ...state,
      loading: false,
      error,
    })),
    on(qcropActions.setToken, (state, { token }) => ({
      ...state,
      token,
    })),
    on(qcropActions.setCompanyId, (state, { companyId }) => ({
      ...state,
      companyId,
    })),
    on(qcropActions.setToken, (state, { token }) => ({
      ...state,
      token,
    }))
  ),
});

export function mergeQcrop(
  oldQcrop: QcropModel | null,
  frames: Frame[]
): QcropModel | null {
  if (!oldQcrop) {
    return null;
  }

  // Create a map of frames by templateId for quick lookup
  const framesMap = new Map<string, Frame>();
  for (const frame of frames) {
    framesMap.set(frame.templateId, frame);
  }

  // Clone the oldCrop to avoid mutating the original object
  return {
    ...oldQcrop,
    templates: oldQcrop.templates.map(template => {
      // Check if there is a new frame for the current template
      const newFrame = framesMap.get(template.id);
      if (newFrame) {
        // Replace the old frame with the new frame
        return {
          ...template,
          frame: newFrame,
          modified: null,
        };
      }
      // If there is no new frame, return the template as is
      return template;
    }),
  };
}

function removeUiProperties(qcrop: QcropModel | null): QcropModel | null {
  if (!qcrop) {
    return null;
  } else {
    return {
      ...qcrop,
      templates: qcrop.templates.map(template => {
        // Destructure to remove the 'visible' property
        const {
          visible,
          belowMinSize,
          relativeMinSize,
          active,
          ...templateWithoutUi
        } = template as UiTemplate;
        return templateWithoutUi;
      }),
      focalPoints: qcrop.focalPoints.map(focalPoint => {
        const { visible, active, ...focalPointWithoutUi } = focalPoint;
        return focalPointWithoutUi;
      }),
    };
  }
}
